/**
 * PublicAlertRequestBlock (Declaration & Notification Slice 2, #497).
 *
 * The operator-facing PUBLIC-ALERT-REQUEST surface, mounted inside the incident work
 * surface as a PARALLEL SIBLING to the declaration + evacuation blocks — NOT a
 * child/precondition of either. In Ontario a public alert is ISSUED only by the PEOC
 * (Ontario PERP §6); this surface RECORDS a request on behalf of the requesting authority
 * and routes it to the PEOC (records-not-confers — the COP requests; it never issues).
 *
 * BOTH the municipal and First-Nation paths are wired (each is a request TO the PEOC — a
 * First-Nation request is NEVER a direct NAAD/NPAS issuer). The FN branch does NOT carry a
 * single-head (Head of Council) affordance — a First Nation requests via its Band Council.
 *
 * FOREIGN-VALUE GUARD: the hazard/incident reference renders ONLY the COP's OWN doctrine-safe
 * identity — the linked incident's incident_number + the doctrine-safe HAZARD_LABEL map. A
 * hazard value NOT in the map falls back to the neutral 'Hazard' — NEVER the raw value. NO raw
 * CAP field (event / eventCode / severity / category / area_desc) or NPAS/NAAD identifier ever
 * reaches this component.
 */
import { useCallback, useEffect, useState, type JSX } from 'react';
import {
  REQUEST_PUBLIC_ALERT_LABEL,
  RESCIND_PUBLIC_ALERT_REQUEST_LABEL,
  PUBLIC_ALERT_REQUEST_HEADING,
  PUBLIC_ALERT_REQUEST_PEOC_COPY,
  PUBLIC_ALERT_AUTHORITY_KIND_LABELS,
  PUBLIC_ALERT_REQUESTER_ROLE,
  PEOC_DUTY_OFFICER_PHONE,
  type PublicAlertAuthorityKind,
  type PublicAlertRequestRecord,
} from '@tsr/shared-types';
import {
  fetchPublicAlertRequests,
  requestPublicAlert,
  rescindPublicAlertRequest,
} from '../api';
import { getSessionId } from '../lib/operatorActions';
import { showToast } from '../lib/toast';

const inputCls =
  'w-full rounded border border-slate-700 bg-slate-900/70 px-2 py-1 text-[12px] text-slate-100 focus:border-cyan-600 focus:outline-none';

/**
 * Doctrine-safe hazard labels (the COP's OWN enum → a human label). The FOREIGN-VALUE GUARD:
 * a hazard value NOT in this map falls back to the neutral 'Hazard' — NEVER the raw value, so a
 * raw CAP event string can never leak onto the surface.
 */
const HAZARD_LABEL: Record<string, string> = {
  wildfire: 'Wildfire',
  flood: 'Flood',
  ice_storm: 'Ice Storm',
  cap_warning: 'Public Alert',
  multi_hazard: 'Multi-Hazard',
  other: 'Incident',
};
function hazardLabel(hazardType: string | null | undefined): string {
  if (!hazardType) return '—';
  return HAZARD_LABEL[hazardType] ?? 'Hazard';
}

function whenLabel(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

export function PublicAlertRequestBlock({
  incidentId,
  incidentNumber,
  hazardType,
  defaultAuthorityName,
  evacuationOrderId,
  defaultAuthorityKind,
}: {
  /** The incident this request is a PARALLEL SIBLING to. Optional — the #461 evac-order
   *  mount has no incident of its own (it links the order instead). */
  incidentId?: string;
  /** The COP's OWN incident identifier (doctrine-safe) — the only incident reference rendered. */
  incidentNumber?: string | null;
  /** The COP's OWN hazard enum — rendered ONLY through HAZARD_LABEL (never raw). */
  hazardType?: string | null;
  /** Optional pre-seed for the requesting jurisdiction (operator can override). */
  defaultAuthorityName?: string | null;
  /** #461 — when raised FROM an evacuation order, the request links back to it (provenance)
   *  and the block matches its existing request by evacuation_order_id (not incident_id). */
  evacuationOrderId?: string;
  /** #461 — pre-seed the requesting-authority KIND from the order's issuing authority
   *  (operator can still toggle). */
  defaultAuthorityKind?: PublicAlertAuthorityKind;
}): JSX.Element {
  const [request, setRequest] = useState<PublicAlertRequestRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [contactPhone, setContactPhone] = useState<string | null>(null);

  // Request-form fields.
  const [authorityKind, setAuthorityKind] = useState<PublicAlertAuthorityKind>(
    defaultAuthorityKind ?? 'municipal',
  );
  const [authorityName, setAuthorityName] = useState(defaultAuthorityName ?? '');
  const [requesterName, setRequesterName] = useState('');
  const [callConfirmed, setCallConfirmed] = useState(false);
  const [callNotes, setCallNotes] = useState('');

  const refresh = useCallback(() => {
    let alive = true;
    setLoading(true);
    void fetchPublicAlertRequests()
      .then((all) => {
        if (!alive) return;
        // Show the request tied to THIS surface: the #461 evac-order mount matches by the
        // provenance link (evacuation_order_id); the incident mount matches by incident_id.
        setRequest(
          all.find((r) =>
            evacuationOrderId
              ? r.evacuation_order_id === evacuationOrderId
              : incidentId
                ? r.incident_id === incidentId
                : false,
          ) ?? null,
        );
      })
      .catch(() => {
        /* a read failure leaves the block in its empty state */
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [incidentId, evacuationOrderId]);

  useEffect(() => refresh(), [refresh]);

  const doRequest = (): void => {
    if (busy || authorityName.trim().length === 0) return;
    setBusy(true);
    void requestPublicAlert(
      {
        authority_kind: authorityKind,
        requesting_authority_name: authorityName.trim(),
        requester_name: requesterName.trim() || undefined,
        incident_id: incidentId,
        evacuation_order_id: evacuationOrderId,
        hazard_type: hazardType ?? undefined,
        call_confirmed: callConfirmed,
        call_notes: callNotes.trim() || undefined,
      },
      getSessionId(),
    )
      .then((res) => {
        setContactPhone(res.contact_phone);
        setRequest(res.request);
        setFormOpen(false);
        showToast(
          `Public alert requested — ${res.request.request_number} · routed to the PEOC via ${res.delivery_channel}.`,
          { variant: 'success' },
        );
      })
      .catch((e) =>
        showToast((e as Error).message || 'Could not request a public alert.', { variant: 'error' }),
      )
      .finally(() => setBusy(false));
  };

  const doRescind = (): void => {
    if (busy || !request) return;
    setBusy(true);
    void rescindPublicAlertRequest(request.id, getSessionId())
      .then((res) => {
        setContactPhone(res.contact_phone);
        setRequest(res.request);
        showToast(`Request ${res.request.request_number} rescinded.`, { variant: 'success' });
      })
      .catch((e) => showToast((e as Error).message || 'Could not rescind.', { variant: 'error' }))
      .finally(() => setBusy(false));
  };

  const phoneToShow = contactPhone ?? PEOC_DUTY_OFFICER_PHONE;
  const requesterFieldLabel = `Requested by (${PUBLIC_ALERT_REQUESTER_ROLE[authorityKind]})`;

  return (
    <section data-testid="public-alert-request-block" className="space-y-1.5">
      <h4 className="text-[10px] uppercase tracking-wider text-slate-400">
        {PUBLIC_ALERT_REQUEST_HEADING}
      </h4>
      <p className="text-[10px] text-slate-500">
        {PUBLIC_ALERT_REQUEST_PEOC_COPY.issuer_note} {PUBLIC_ALERT_REQUEST_PEOC_COPY.request_note}
      </p>

      {loading ? (
        <p className="text-[11px] text-slate-500">Loading…</p>
      ) : !request ? (
        /* ── No request yet — the Request affordance ─────────────────────── */
        !formOpen ? (
          <button
            type="button"
            data-testid="request-public-alert-open"
            onClick={() => setFormOpen(true)}
            className="w-full rounded border border-cyan-500/60 py-1.5 text-[11px] text-cyan-200 hover:bg-cyan-600/20"
          >
            {REQUEST_PUBLIC_ALERT_LABEL}
          </button>
        ) : (
          <div className="space-y-1.5 rounded border border-slate-700 bg-slate-900/50 p-2">
            {/* Authority-kind toggle — municipal vs First Nation (both are requests TO the PEOC). */}
            <div className="flex gap-1" role="group" aria-label="Requesting authority kind">
              {(['municipal', 'first_nation'] as const).map((k) => (
                <button
                  key={k}
                  type="button"
                  data-testid={`authority-kind-${k}`}
                  onClick={() => setAuthorityKind(k)}
                  className={`flex-1 rounded border py-1 text-[11px] ${
                    authorityKind === k
                      ? 'border-cyan-500 bg-cyan-600/25 text-cyan-100'
                      : 'border-slate-700 text-slate-400 hover:bg-slate-800'
                  }`}
                >
                  {PUBLIC_ALERT_AUTHORITY_KIND_LABELS[k]}
                </button>
              ))}
            </div>
            <label className="block text-[10px] text-slate-400">
              {authorityKind === 'first_nation' ? 'Requesting First Nation' : 'Requesting municipality'}
              <input
                data-testid="request-authority-name"
                value={authorityName}
                onChange={(e) => setAuthorityName(e.target.value)}
                placeholder={authorityKind === 'first_nation' ? 'e.g. Eabametoong First Nation' : 'e.g. City of Kenora'}
                className={inputCls}
              />
            </label>
            <label className="block text-[10px] text-slate-400">
              {requesterFieldLabel}
              <input
                data-testid="request-requester-name"
                value={requesterName}
                onChange={(e) => setRequesterName(e.target.value)}
                placeholder={
                  authorityKind === 'first_nation'
                    ? 'on behalf of the Band Council (optional)'
                    : 'name / title (optional)'
                }
                className={inputCls}
              />
            </label>
            {/* PEOC routing — the phone the operator can call to flag the incoming request. */}
            <p className="text-[10px] leading-snug text-slate-400">
              {PUBLIC_ALERT_REQUEST_PEOC_COPY.live_route}. PEOC Duty Officer (24/7):{' '}
              <span className="font-mono text-cyan-200">{phoneToShow}</span>
            </p>
            <label className="block text-[10px] text-slate-400">
              Call notes (optional)
              <textarea
                data-testid="request-call-notes"
                value={callNotes}
                onChange={(e) => setCallNotes(e.target.value)}
                rows={2}
                className={inputCls}
              />
            </label>
            <label className="flex items-center gap-1.5 text-[11px] text-slate-300">
              <input
                type="checkbox"
                data-testid="request-call-confirmed"
                checked={callConfirmed}
                onChange={(e) => setCallConfirmed(e.target.checked)}
              />
              Confirm the PEOC Duty-Officer call to flag this request was placed
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="request-public-alert-submit"
                onClick={doRequest}
                disabled={busy || authorityName.trim().length === 0}
                className="flex-1 rounded bg-cyan-600 py-1.5 text-[11px] font-medium text-white hover:bg-cyan-500 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busy ? 'Requesting…' : REQUEST_PUBLIC_ALERT_LABEL}
              </button>
              <button
                type="button"
                onClick={() => setFormOpen(false)}
                className="rounded border border-slate-700 px-3 py-1.5 text-[11px] text-slate-400 hover:bg-slate-800"
              >
                Cancel
              </button>
            </div>
          </div>
        )
      ) : (
        /* ── A request exists — record + actions ─────────────────────────── */
        <div className="space-y-1.5" data-testid="public-alert-request-detail">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[12px] text-slate-200">{request.request_number}</span>
            <span
              className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
                request.status === 'active'
                  ? 'bg-cyan-600/25 text-cyan-200'
                  : 'bg-slate-700/50 text-slate-400'
              }`}
              data-testid="request-status"
            >
              {request.status === 'active' ? 'Active' : 'Rescinded'}
            </span>
          </div>
          <dl className="grid grid-cols-[7rem_1fr] gap-y-0.5 text-[11px]">
            <dt className="text-slate-400">Authority</dt>
            <dd className="text-slate-200">
              {request.requesting_authority_name}
              {' · '}
              {PUBLIC_ALERT_AUTHORITY_KIND_LABELS[request.authority_kind]}
            </dd>
            <dt className="text-slate-400">Requested by</dt>
            <dd className="text-slate-200">
              {PUBLIC_ALERT_REQUESTER_ROLE[request.authority_kind]}
              {request.requester_name ? ` — ${request.requester_name}` : ''}
            </dd>
            {/* FOREIGN-VALUE GUARD: incident + hazard render ONLY the COP's own identity. */}
            <dt className="text-slate-400">Hazard</dt>
            <dd className="text-slate-300" data-testid="request-hazard">
              {hazardLabel(request.hazard_type)}
            </dd>
            <dt className="text-slate-400">Incident</dt>
            <dd className="text-slate-300" data-testid="request-incident">
              {incidentNumber ?? '—'}
            </dd>
            <dt className="text-slate-400">Requested</dt>
            <dd className="text-slate-300">{whenLabel(request.requested_at)}</dd>
            {request.status === 'rescinded' && (
              <>
                <dt className="text-slate-400">Rescinded</dt>
                <dd className="text-slate-300">{whenLabel(request.rescinded_at)}</dd>
              </>
            )}
            <dt className="text-slate-400">PEOC</dt>
            <dd className="text-slate-300" data-testid="request-peoc-state">
              {request.peoc_notified_at
                ? `Routed ${whenLabel(request.peoc_notified_at)}`
                : 'Not yet routed'}
            </dd>
          </dl>

          {request.status === 'active' && (
            <p className="text-[10px] text-slate-400">
              PEOC Duty Officer (24/7):{' '}
              <a
                href={`tel:${phoneToShow.replace(/[^0-9]/g, '')}`}
                className="font-mono text-cyan-200 underline"
                data-testid="request-peoc-phone"
              >
                {phoneToShow}
              </a>
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <a
              data-testid="request-pdf-link"
              href={`/api/public-alert-requests/${request.id}/pdf`}
              target="_blank"
              rel="noreferrer"
              className="rounded border border-slate-600 px-2 py-1 text-[11px] text-slate-300 hover:bg-slate-800"
            >
              Download request PDF
            </a>
            {request.status === 'active' && (
              <button
                type="button"
                data-testid="rescind-public-alert-request"
                onClick={doRescind}
                disabled={busy}
                className="rounded border border-slate-600 px-2 py-1 text-[11px] text-slate-300 hover:bg-slate-800 disabled:opacity-50"
              >
                {RESCIND_PUBLIC_ALERT_REQUEST_LABEL}
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
